import React, { Component, type ReactNode } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary léger pour les sections du dashboard comptable.
 * Isole les crashs individuels sans faire tomber toute la page.
 */
export class WidgetErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.warn('[WidgetErrorBoundary]', error.message, info.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center p-6 bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-800 min-h-[120px]">
          <AlertCircle className="h-8 w-8 text-red-400 mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {this.props.fallbackTitle || 'Erreur de chargement'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center max-w-[200px]">
            Cette section a rencontré un problème
          </p>
          <button
            onClick={this.handleRetry}
            className="mt-3 inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400"
          >
            <RefreshCw className="h-3 w-3" /> Réessayer
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
